import { useContext } from 'react'

import { Collaborate } from '@/assets/icons'
import { Banner, type BannerLayout } from '@/components/banner/Banner'
import { Button } from '@/components/button/Button'
import { CollaborationOrgPromptPopover } from '@/collaboration/components/CollaborationOrgPromptPopover'
import { useCollaborationUpsell } from '@/collaboration/hooks/useCollaborationUpsell'
import { AuthContext } from '@/context/auth-context'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { MatterCollaborationEntrySource } from '@/analytics/enums/matter-collaboration.enums'
import { getCollaborationOrganizationId } from '@/analytics/utils/matter-collaboration-analytics'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { useMatterBannerDismissal } from '@/matter/hooks/useMatterBannerDismissal'
import { MatterBannerKind } from '@/matter/store/schemas'
import { useMatterOverviewBanner } from '@/matter/components/matter-overview/useMatterOverviewBanner'
import { useAppDispatch } from '@/store/store-hooks'

type MatterOverviewBannerProps = {
  /** `card` for the narrow context-rail column, `row` when the banner spans the main column. @default 'row' */
  layout?: BannerLayout
}

export function MatterOverviewBanner({ layout = 'row' }: MatterOverviewBannerProps) {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const authContext = useContext(AuthContext)
  const { handleUpsellClick } = useCollaborationUpsell()
  const { showCollaborationBanner, matter, isOrgAccount } = useMatterOverviewBanner()
  const { dismissBanner } = useMatterBannerDismissal(matter?.id)

  if (!showCollaborationBanner || !matter) return null

  const handleAddMembers = () => {
    const organizationId = getCollaborationOrganizationId(authContext?.userAccountData)
    trackEvent(AnalyticsEvent.MatterCollaborationBannerAddUserClicked, { matterId: matter.id, organizationId, isOrgAccount })
    if (!isOrgAccount) return

    dispatch(
      MattersActions.openDialog({
        dialogType: MatterDialogType.ManageMembers,
        matterId: matter.id,
        source: MatterCollaborationEntrySource.CollaborationBanner,
      })
    )
  }

  const addMembersButton = (
    <Button variant="tonal" color="primary" size="xs" onClick={handleAddMembers}>
      Add Members
    </Button>
  )

  const collaborateChip =
    layout === 'card' ? (
      <span className="flex items-center justify-center rounded-full bg-brand-100 p-1 text-brand-800">
        <Collaborate className="h-4 w-4" />
      </span>
    ) : (
      <span className="flex items-center justify-center rounded bg-brand-100 p-1.5 text-brand-800">
        <Collaborate className="h-5 w-5" />
      </span>
    )

  return (
    <Banner
      layout={layout}
      icon={collaborateChip}
      title="Share this matter with your team"
      description={layout === 'card' ? 'Everyone works from the same source of truth' : undefined}
      onDismiss={() => dismissBanner(MatterBannerKind.Collaboration)}
      action={
        isOrgAccount ? (
          addMembersButton
        ) : (
          <CollaborationOrgPromptPopover onCreateOrganization={handleUpsellClick}>{addMembersButton}</CollaborationOrgPromptPopover>
        )
      }
    />
  )
}
